import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import SEOHead from '../components/common/SEOHead';
import { toast } from 'sonner';
import { ArrowRight, Check } from 'lucide-react';

const REGIONS = [
    { slug: 'durham', name: 'Durham Region' },
    { slug: 'hamilton', name: 'Hamilton' },
    { slug: 'halton', name: 'Halton Region' },
    { slug: 'brantford', name: 'Brantford' },
    { slug: 'brant-county', name: 'Brant County' },
    { slug: 'niagara', name: 'Niagara Region' },
    { slug: 'undecided', name: 'Not sure yet — help me decide' },
];
const STAGES = [
    { k: 'researching', l: 'Researching' },
    { k: 'buying', l: 'Buying' },
    { k: 'selling', l: 'Selling' },
    { k: 'investing', l: 'Investing' },
];
const TIMELINES = [
    { k: '0-3m', l: '0–3 months' },
    { k: '3-6m', l: '3–6 months' },
    { k: '6-12m', l: '6–12 months' },
    { k: '12m+', l: '12+ months' },
];

export default function StrategySession() {
    const [params] = useSearchParams();
    const advisorSessionId = params.get('advisor_session') || null;
    const [form, setForm] = useState({
        name: '',
        email: '',
        phone: '',
        region_slug: params.get('region') || 'undecided',
        stage: 'researching',
        timeline: '3-6m',
        notes: '',
    });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

    async function submit(e) {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        try {
            await api.post('/strategy-session', { ...form, advisor_session_id: advisorSessionId });
            setDone(true);
            toast.success('Session request received. We\'ll be in touch within one business day.');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Something went wrong. Try again.');
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="bg-white">
            <SEOHead
                title="Book a Regional Strategy Session"
                description="Free 30-minute video call with a Southern Ontario neighbourhood specialist. Independent analysis, no listings, no pressure."
                path="/strategy-session"
            />

            <section className="border-b border-neutral-200">
                <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-16 pb-14 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-6">
                        <div className="text-[11px] tracking-[0.24em] uppercase font-mono-data text-[#2F6B47]">
                            Regional Strategy Session
                        </div>
                        <h1
                            className="font-editorial mt-6 tracking-editorial"
                            style={{ fontSize: 'clamp(40px, 5.4vw, 72px)', lineHeight: 1.04, letterSpacing: '-0.025em' }}
                        >
                            A 30-minute call.
                            <br />
                            <span className="italic text-neutral-500">A clearer decision.</span>
                        </h1>
                        <p className="mt-6 text-[16px] leading-relaxed text-neutral-700 max-w-lg font-editorial">
                            Independent, no-pressure analysis of your move. We&apos;ll walk through affordability,
                            appreciation, schools, transit and lifestyle fit for the neighbourhoods you&apos;re considering.
                        </p>
                        <ul className="mt-10 space-y-4 max-w-md">
                            {[
                                'Free · No obligation · No listings',
                                'Data-first: we work from the same intelligence platform you see',
                                'Direct video call — held within one business day',
                            ].map((p) => (
                                <li key={p} className="flex items-start gap-3 text-[15px] text-neutral-800">
                                    <Check className="w-4 h-4 text-[#2F6B47] mt-1 flex-shrink-0" strokeWidth={1.5} />
                                    <span>{p}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="lg:col-span-6">
                        {done ? (
                            <div className="border border-brand-navy p-10 bg-[#F7F7F5]" data-testid="strategy-confirmed">
                                <div className="text-[11px] tracking-[0.24em] uppercase font-mono-data text-[#2F6B47]">Confirmed</div>
                                <div className="font-editorial text-[32px] mt-4 tracking-editorial leading-tight">
                                    Thank you, {form.name.split(' ')[0] || 'friend'}.
                                </div>
                                <p className="mt-4 text-[15px] text-neutral-700 leading-relaxed">
                                    A specialist will reach out to <span className="font-mono-data text-[13px]">{form.email}</span> within one business day
                                    to schedule your 30-minute session.
                                </p>
                                <div className="mt-8">
                                    <Link to="/" className="link-editorial text-[14px]">← Back to home</Link>
                                </div>
                            </div>
                        ) : (
                            <form
                                onSubmit={submit}
                                className="border border-neutral-200 p-8 lg:p-10"
                               
                                data-testid="strategy-form"
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <Field label="Your name" required>
                                        <input
                                            required
                                            data-testid="strategy-name"
                                            value={form.name}
                                            onChange={set('name')}
                                            placeholder="Jane Doe"
                                            className="w-full border border-neutral-300 px-3 py-2.5 text-[15px] focus:border-black outline-none"
                                           
                                        />
                                    </Field>
                                    <Field label="Email" required>
                                        <input
                                            required
                                            type="email"
                                            data-testid="strategy-email"
                                            value={form.email}
                                            onChange={set('email')}
                                            placeholder="you@company.ca"
                                            className="w-full border border-neutral-300 px-3 py-2.5 text-[15px] focus:border-black outline-none"
                                           
                                        />
                                    </Field>
                                </div>

                                <Field label="Phone (optional)" className="mt-5">
                                    <input
                                        data-testid="strategy-phone"
                                        value={form.phone}
                                        onChange={set('phone')}
                                        placeholder="(416) 555-0100"
                                        className="w-full border border-neutral-300 px-3 py-2.5 text-[15px] focus:border-black outline-none"
                                       
                                    />
                                </Field>

                                <Field label="Region of interest" className="mt-5">
                                    <select
                                        data-testid="strategy-region"
                                        value={form.region_slug}
                                        onChange={set('region_slug')}
                                        className="w-full border border-neutral-300 px-3 py-2.5 text-[15px] bg-white"
                                       
                                    >
                                        {REGIONS.map((r) => <option key={r.slug} value={r.slug}>{r.name}</option>)}
                                    </select>
                                </Field>

                                <Field label="Where are you in the process?" className="mt-5">
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                        {STAGES.map((s) => (
                                            <button
                                                type="button"
                                                key={s.k}
                                                data-testid={`strategy-stage-${s.k}`}
                                                onClick={() => setForm((f) => ({ ...f, stage: s.k }))}
                                                className={`px-3 py-2.5 border text-[13px] ${form.stage === s.k ? 'bg-brand-navy text-white border-black' : 'border-neutral-300 hover:border-brand-navy'}`}
                                               
                                            >
                                                {s.l}
                                            </button>
                                        ))}
                                    </div>
                                </Field>

                                <Field label="Your timeline" className="mt-5">
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                        {TIMELINES.map((t) => (
                                            <button
                                                type="button"
                                                key={t.k}
                                                data-testid={`strategy-timeline-${t.k}`}
                                                onClick={() => setForm((f) => ({ ...f, timeline: t.k }))}
                                                className={`px-3 py-2.5 border text-[13px] font-mono-data ${form.timeline === t.k ? 'bg-brand-navy text-white border-black' : 'border-neutral-300 hover:border-brand-navy'}`}
                                               
                                            >
                                                {t.l}
                                            </button>
                                        ))}
                                    </div>
                                </Field>

                                <Field label="Anything specific we should prepare?" className="mt-5">
                                    <textarea
                                        data-testid="strategy-notes"
                                        rows={3}
                                        value={form.notes}
                                        onChange={set('notes')}
                                        placeholder="Optional. Neighbourhoods you're considering, questions you want answered, etc."
                                        className="w-full border border-neutral-300 px-3 py-2.5 text-[15px] focus:border-black outline-none resize-none"
                                       
                                    />
                                </Field>

                                <button
                                    type="submit"
                                    disabled={busy}
                                    data-testid="strategy-submit"
                                    className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-brand-navy text-white px-6 py-3.5 text-[14px] font-medium hover:bg-brand-gold hover:text-brand-navy border border-brand-navy transition-colors disabled:opacity-60"
                                    style={{ letterSpacing: '0.02em' }}
                                >
                                    {busy ? 'Sending…' : 'Book my strategy session'}
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                                <div className="mt-3 text-[11px] uppercase tracking-[0.16em] text-neutral-400 font-mono-data text-center">
                                    Free · No obligation · One business day response
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}

function Field({ label, required, className = '', children }) {
    return (
        <div className={className}>
            <label className="text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-mono-data">
                {label} {required && <span className="text-[#2F6B47]">*</span>}
            </label>
            <div className="mt-2">{children}</div>
        </div>
    );
}
